import _ from "lodash";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import useRowClassName from "../../hooks/useRowClassName";
import IEntityFieldsProps from "../../interfaces/IEntityFieldsProps";
import { ISaleCompState, SaleCompDealProfile, SaleCompPropertyType, SaleCompSource } from "../../interfaces/ISaleComp";
import { saleCompDealProfileToString, saleCompPropertyTypeToString, saleCompSourceToString } from "../../utils/enumUtils";
import commonStyles from "../Common.module.css";
import editStyles from "../Edit.module.css";
import AddressInput from "../inputs/AddressInput";
import DateInput from "../inputs/DateInput";
import NumberInput from "../inputs/NumberInput";
import PipedriveOrIndividualCompanyInput from "../inputs/PipedriveOrIndividualCompanyInput";
import SelectInput from "../inputs/SelectInput";
import StringInput from "../inputs/StringInput";
import TextInput from "../inputs/TextInput";

function SaleCompFields({ formik }: IEntityFieldsProps<ISaleCompState>) {
    const rowClassName = useRowClassName();

    const onParcelSizeAcresChange = useCallback((value: number | null) => ({
        grossPerAcre: calculateGrossPerAcre(formik.values.salePrice, value),
    }), [formik.values]);

    const onLandSizeUsableChange = useCallback((value: number | null) => ({
        pricePerUsableAcre: calculatePricePerUsableAcre(formik.values.salePrice, value),
    }), [formik.values]);

    const onBuildingSizeChange = useCallback((value: number | null) => ({
        buildingPerSqFt: calculateBuildingPerSqFt(formik.values.salePrice, value),
    }), [formik.values]);

    const onNoiChange = useCallback((value: number | null) => ({
        capRate: calculateCapRate(formik.values.salePrice, value),
    }), [formik.values]);

    const onSalePriceChange = useCallback((value: number | null) => ({
        grossPerAcre: calculateGrossPerAcre(value, formik.values.parcelSizeAcres),
        pricePerUsableAcre: calculatePricePerUsableAcre(value, formik.values.landSizeUsable),
        buildingPerSqFt: calculateBuildingPerSqFt(value, formik.values.buildingSize),
        capRate: calculateCapRate(value, formik.values.noi),
    }), [formik.values]);

    return (
        <div className={editStyles.container}>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Sale Details</div>
                <div className={rowClassName}>
                    <AddressInput formik={formik} label="ADDRESS" name="address" />
                </div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="GROSS $/ACRE" name="grossPerAcre" />
                    <NumberInput formik={formik} label="USABLE $/ACRE" name="pricePerUsableAcre" />
                    <NumberInput formik={formik} label="BUILDING $/PSF" name="buildingPerSqFt" />
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <NumberInput formik={formik} label="SALE PRICE ($)" name="salePrice" onChange={onSalePriceChange} />
                            <DateInput formik={formik} label="DATE SOLD" name="saleDate" />
                        </div>
                    </div>
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <PipedriveOrIndividualCompanyInput formik={formik} label="BUYER" name="buyerName" isIndividualName="isBuyerIndividual" />
                            <PipedriveOrIndividualCompanyInput formik={formik} label="SELLER" name="sellerName" isIndividualName="isSellerIndividual" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Other Terms</div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="CAP RATE (%)" name="capRate" />
                    <NumberInput formik={formik} label="NOI ($)" name="noi" onChange={onNoiChange} />
                    <SelectInput formik={formik} label="MARKETED" name="isMarketed" valueAsCorrectType={value => value === "yes"} correctTypeAsValue={value => value ? "yes" : "no"}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.rowGroup}>
                        <div className={rowClassName}>
                            <PipedriveOrIndividualCompanyInput formik={formik} label="BROKERAGE" name="brokerageName" isIndividualName="isBrokerageIndividual" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Property Details</div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="USABLE LAND SIZE (ACRES)" name="landSizeUsable" onChange={onLandSizeUsableChange} />
                    <NumberInput formik={formik} label="PARCEL SIZE (ACRES)" name="parcelSizeAcres" onChange={onParcelSizeAcresChange} />
                    <NumberInput formik={formik} label="BUILDING SIZE (SQ-FT)" name="buildingSize" onChange={onBuildingSizeChange} />
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="ZONING" name="zoning" />
                            <SelectInput formik={formik} label="PROPERTY TYPE" name="propertyType" valueAsCorrectType={value => value as SaleCompPropertyType}>
                                <option value="">----</option>
                                <option value={SaleCompPropertyType.RawLand}>{saleCompPropertyTypeToString(SaleCompPropertyType.RawLand)}</option>
                                <option value={SaleCompPropertyType.DataCenterLand}>{saleCompPropertyTypeToString(SaleCompPropertyType.DataCenterLand)}</option>
                                <option value={SaleCompPropertyType.DistributionDevelopmentLand}>{saleCompPropertyTypeToString(SaleCompPropertyType.DistributionDevelopmentLand)}</option>
                                <option value={SaleCompPropertyType.IOSNoBuilding}>{saleCompPropertyTypeToString(SaleCompPropertyType.IOSNoBuilding)}</option>
                                <option value={SaleCompPropertyType.IOSWithBuilding}>{saleCompPropertyTypeToString(SaleCompPropertyType.IOSWithBuilding)}</option>
                                <option value={SaleCompPropertyType.SmallBay}>{saleCompPropertyTypeToString(SaleCompPropertyType.SmallBay)}</option>
                                <option value={SaleCompPropertyType.Distribution}>{saleCompPropertyTypeToString(SaleCompPropertyType.Distribution)}</option>
                                <option value={SaleCompPropertyType.SelfStorage}>{saleCompPropertyTypeToString(SaleCompPropertyType.SelfStorage)}</option>
                                <option value={SaleCompPropertyType.RVBoatStorage}>{saleCompPropertyTypeToString(SaleCompPropertyType.RVBoatStorage)}</option>
                                <option value={SaleCompPropertyType.TruckTerminal}>{saleCompPropertyTypeToString(SaleCompPropertyType.TruckTerminal)}</option>
                                <option value={SaleCompPropertyType.Other}>{saleCompPropertyTypeToString(SaleCompPropertyType.Other)}</option>
                            </SelectInput>
                        </div>
                    </div>
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <SelectInput formik={formik} label="DEAL PROFILE" name="dealProfile" valueAsCorrectType={value => value as SaleCompDealProfile}>
                                <option value="">----</option>
                                <option value={SaleCompDealProfile.UserSale}>{saleCompDealProfileToString(SaleCompDealProfile.UserSale)}</option>
                                <option value={SaleCompDealProfile.ValueAdd}>{saleCompDealProfileToString(SaleCompDealProfile.ValueAdd)}</option>
                                <option value={SaleCompDealProfile.Development}>{saleCompDealProfileToString(SaleCompDealProfile.Development)}</option>
                                <option value={SaleCompDealProfile.Core}>{saleCompDealProfileToString(SaleCompDealProfile.Core)}</option>
                                <option value={SaleCompDealProfile.CorePlus}>{saleCompDealProfileToString(SaleCompDealProfile.CorePlus)}</option>
                            </SelectInput>
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Internal</div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="COMP SOURCE" name="compSource" valueAsCorrectType={value => value as SaleCompSource}>
                        <option value="">----</option>
                        <option value={SaleCompSource.PropertyOwner}>{saleCompSourceToString(SaleCompSource.PropertyOwner)}</option>
                        <option value={SaleCompSource.TransactionBroker}>{saleCompSourceToString(SaleCompSource.TransactionBroker)}</option>
                        <option value={SaleCompSource.PublicRecord}>{saleCompSourceToString(SaleCompSource.PublicRecord)}</option>
                        <option value={SaleCompSource.ThirdPartyBroker}>{saleCompSourceToString(SaleCompSource.ThirdPartyBroker)}</option>
                        <option value={SaleCompSource.Costar}>{saleCompSourceToString(SaleCompSource.Costar)}</option>
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <TextInput formik={formik} label="NOTES" name="notes" placeholder="Enter notes..." />
                </div>
            </div>
        </div>
    );
}

const calculateGrossPerAcre = (salePrice: number | null, parcelSizeAcres: number | null): number => {
    return salePrice && parcelSizeAcres ? _.round(salePrice / parcelSizeAcres, 2) : 0;
}

const calculatePricePerUsableAcre = (salePrice: number | null, landSizeUsable: number | null): number | null => {
    return salePrice && landSizeUsable ? _.round(salePrice / landSizeUsable, 2) : null;
}

const calculateBuildingPerSqFt = (salePrice: number | null, buildingSize: number | null): number | null => {
    return salePrice && buildingSize ? _.round(salePrice / buildingSize, 2) : null;
}

const calculateCapRate = (salePrice: number | null, noi: number | null): number | null => {
    return noi && salePrice ? _.round(noi / salePrice * 100, 2) : null;
}

export default observer(SaleCompFields);